import * as React from 'react';
import { QueryClient, QueryClientContext, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { AuthContext } from '@/contexts/auth-context';
import type { ReelCosts, ReferralInfo, ReferralTerms } from './types';

// The share card sits inside studio flows that some pages (and their tests)
// render without a QueryClientProvider. There the hooks stay idle on a spare
// client: no prices or referral line, and no extra requests.
const idleClient = new QueryClient();
/** Signed in, or false where no AuthProvider is above (same idle rule). */
export function useSignedIn(): boolean {
  return Boolean(React.useContext(AuthContext)?.isAuthenticated);
}

function useClient(): { client: QueryClient; live: boolean } {
  const ctx = React.useContext(QueryClientContext);
  return { client: ctx ?? idleClient, live: Boolean(ctx) };
}

/** What each studio costs, from the live config. Empty (no prices shown) if it can't load. */
export function useReelCosts() {
  const { client, live } = useClient();
  return useQuery<ReelCosts>({
    enabled: live,
    queryKey: ['reel-costs'],
    queryFn: async () => {
      const res = await api.getReelCosts();
      return res.success && res.data ? res.data.costs : {};
    },
    staleTime: 5 * 60 * 1000,
  }, client);
}

export function useReferralTerms() {
  const { client, live } = useClient();
  return useQuery<ReferralTerms>({
    enabled: live,
    queryKey: ['referral-terms'],
    queryFn: async () => {
      const res = await api.getReferralTerms();
      return res.success && res.data ? res.data : { enabled: false, credits: 0 };
    },
    staleTime: 5 * 60 * 1000,
  }, client);
}

/** The signed-in account's own referral code; null when signed out or off. */
export function useMyReferral() {
  const isAuthenticated = useSignedIn();
  const { client, live } = useClient();
  return useQuery<ReferralInfo | null>({
    queryKey: ['my-referral', isAuthenticated],
    enabled: live && isAuthenticated,
    queryFn: async () => {
      const res = await api.getReferral();
      return res.success && res.data ? res.data : null;
    },
    staleTime: 5 * 60 * 1000,
  }, client);
}
